import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin';
import Sidebar from 'components/sidebar/Sidebar';
import { SidebarContext } from 'contexts/SidebarContext';
import { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import routes from 'routes';
import { useAuth } from 'contexts/AuthContext';
import { getVisibleSidebarRoutes } from 'utils/routeUtils';
import { UserRole } from 'utils/rolePermissions';

// Custom Chakra theme
export default function TrainerDashboard(props: { [x: string]: any }) {
    const { ...rest } = props;
    const location = useLocation();
    const { user } = useAuth();

    // states and functions
    const [fixed] = useState(false);
    const [toggleSidebar, setToggleSidebar] = useState(false);

    // Filter routes for trainer layout only
    const trainerRoutes = routes.filter(route =>
        route.layout === '/trainer'
    );

    // Get visible sidebar routes based on user role and current layout
    const visibleSidebarRoutes = getVisibleSidebarRoutes(trainerRoutes, user?.role);

    // functions for changing the states from components
    const getRoute = () => {
        return location.pathname !== '/trainer/full-screen-maps';
    };

    const getActiveRoute = (routes: RoutesType[]): string => {
        const currentPath = location.pathname;
        const searchParams = new URLSearchParams(location.search);

        // Default route name based on context
        let activeRoute = 'Trainer Dashboard';

        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];
            const fullPath = route.layout + route.path;

            // Handle exact matches first
            if (currentPath === fullPath) {
                return route.name;
            }

            // Handle parameterized routes
            if (route.path.includes(':')) {
                const routePattern = fullPath.replace(/:[^/]+/g, '([^/]+)');
                const regex = new RegExp(`^${routePattern}$`);
                const match = currentPath.match(regex);

                if (match) {
                    // Special handling for calendar routes
                    if (route.path.includes('/calendar/:clientId')) {
                        const clientName = searchParams.get('client_name');
                        if (clientName) {
                            return `${clientName}'s Calendar`;
                        }
                        return 'Client Calendar';
                    }
                    return route.name;
                }
            }

            // Handle wildcard routes
            if (route.path.includes('*')) {
                const pathPattern = route.path.replace('*', '.*');
                const regex = new RegExp(`^${route.layout}${pathPattern}`);
                if (regex.test(currentPath)) {
                    return route.name;
                }
            }
        }

        // Context-aware fallbacks based on URL patterns
        if (currentPath.includes('/calendar/')) {
            const clientName = searchParams.get('client_name');
            if (clientName) {
                return `${clientName}'s Calendar`;
            }
            return 'Client Calendar';
        }

        if (currentPath.includes('/calendar')) {
            return 'Calendar Overview';
        }

        if (currentPath.includes('/my-clients')) {
            return 'My Clients';
        }

        return activeRoute;
    };

    const getActiveNavbar = (routes: RoutesType[]): boolean => {
        let activeNavbar = false;
        const currentPath = location.pathname;

        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];
            const fullPath = route.layout + route.path;

            // Handle exact matches first
            if (currentPath === fullPath) {
                return route.secondary ?? false;
            }

            // Handle parameterized routes
            if (route.path.includes(':')) {
                const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
                const regex = new RegExp(`^${route.layout}${pathPattern}$`);
                if (regex.test(currentPath)) {
                    return route.secondary ?? false;
                }
            }
        }
        return activeNavbar;
    };

    const getActiveNavbarText = (routes: RoutesType[]): string | boolean => {
        let activeNavbar = false;
        const currentPath = location.pathname;

        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];
            const fullPath = route.layout + route.path;

            // Handle exact matches first
            if (currentPath === fullPath) {
                return route.name;
            }

            // Handle parameterized routes
            if (route.path.includes(':')) {
                const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
                const regex = new RegExp(`^${route.layout}${pathPattern}$`);
                if (regex.test(currentPath)) {
                    return route.name;
                }
            }
        }
        return activeNavbar;
    };

    const getRoutes = (routes: RoutesType[]): any => {
        return routes.map((route: RoutesType, key: any) => {
            if (route.layout === '/trainer') {
                // Check if user has permission for this route
                if (route.allowedRoles && user?.role && !route.allowedRoles.includes(user.role as UserRole)) {
                    return null; // User doesn't have permission for this route
                }

                return (
                    <Route path={`${route.path}`} element={route.component} key={key} />
                );
            } else {
                return null;
            }
        });
    };

    document.documentElement.dir = 'ltr';
    const { onOpen } = useDisclosure();

    return (
        <Box>
            <SidebarContext.Provider
                value={{
                    toggleSidebar,
                    setToggleSidebar,
                }}
            >
                <Sidebar routes={visibleSidebarRoutes} display="none" {...rest} />
                <Box
                    float="right"
                    minHeight="100vh"
                    height="100%"
                    overflow="auto"
                    position="relative"
                    maxHeight="100%"
                    w={{ base: '100%', xl: 'calc( 100% - 290px )' }}
                    maxWidth={{ base: '100%', xl: 'calc( 100% - 290px )' }}
                    transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
                    transitionDuration=".2s, .2s, .35s"
                    transitionProperty="top, bottom, width"
                    transitionTimingFunction="linear, linear, ease"
                >
                    <Portal>
                        <Box>
                            <Navbar
                                onOpen={onOpen}
                                logoText={'Trainer Dashboard'}
                                brandText={getActiveRoute(visibleSidebarRoutes)}
                                secondary={getActiveNavbar(visibleSidebarRoutes)}
                                message={getActiveNavbarText(visibleSidebarRoutes)}
                                fixed={fixed}
                                {...rest}
                            />
                        </Box>
                    </Portal>

                    {getRoute() ? (
                        <Box
                            mx="auto"
                            p={{ base: '20px', md: '30px' }}
                            pe="20px"
                            minH="100vh"
                            pt="50px"
                        >
                            <Routes>
                                {getRoutes(trainerRoutes)}
                                <Route
                                    path="/"
                                    element={<Navigate to="/trainer/default" replace />}
                                />
                                {/* 404 route for any unmatched trainer routes */}
                                <Route path="*" element={<Navigate to="/404" replace />} />
                            </Routes>
                        </Box>
                    ) : null}
                    <Box>
                        <Footer />
                    </Box>
                </Box>
            </SidebarContext.Provider>
        </Box>
    );
}
